/**
 * Field — the label / description / error wrapper.
 *
 * Pass children as a function to receive the generated `id` and `aria-describedby`
 * so the control, its hint and its error stay wired together without the caller
 * inventing ids. The message row is always reserved — an error can appear and
 * disappear without the field, or anything under it, moving a pixel.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface FieldRenderProps {
  id: string;
  /** Ready for the control's `aria-describedby`. */
  describedBy: string | undefined;
  invalid: boolean;
}

export interface FieldProps {
  label?: ReactNode;
  /** Quiet helper copy. Replaced by `error` while the field is invalid. */
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Id of the control this labels. Generated when omitted. */
  htmlFor?: string;
  /** Right-aligned slot on the label row — a counter, a "Learn more", a toggle. */
  action?: ReactNode;
  /**
   * Keep the message line reserved even when there is nothing in it. Default
   * `true`, which is what a form wants: an error must never move the control
   * the user just used.
   *
   * Pass `false` where a field cannot be invalid and nothing under it can jump —
   * a filter popover, where five reserved lines are 80px of air in a 272px
   * column. An `error` or a `hint` still renders; only the empty reserve goes.
   */
  reserveMessage?: boolean;
  children: ReactNode | ((props: FieldRenderProps) => ReactNode);
  className?: string;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  htmlFor,
  action,
  reserveMessage = true,
  children,
  className,
}: FieldProps) {
  const generated = useId();
  const id = htmlFor ?? generated;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);
  const describedBy = invalid ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {/* The label row is `min-h-4` so a 12px `leading-none` label and a control
          in the `action` slot share one row height. That reserve puts the
          label's painted top 2px inside the row, which a caller measuring a
          margin *to* a label has to know about — hence the hook, so a surface
          that wants the row to be exactly its line box can say so once instead
          of compensating at every margin. */}
      {label || action ? (
        <div data-field-label-row="" className="flex min-h-4 items-center justify-between gap-2">
          {label ? (
            <label
              htmlFor={id}
              className="text-sm font-medium text-secondary leading-none select-none"
            >
              {label}
              {required ? (
                <>
                  <span aria-hidden="true" className="ml-0.5 text-danger">
                    *
                  </span>
                  <span className="sr-only"> (required)</span>
                </>
              ) : null}
            </label>
          ) : (
            <span />
          )}
          {action ? <div className="shrink-0 leading-none">{action}</div> : null}
        </div>
      ) : null}

      {typeof children === 'function' ? children({ id, describedBy, invalid }) : children}

      {/* The message row is *always* rendered. Validating a form must never move
          the control the user just used, so one line is reserved whether or not
          there is anything to say in it — at `--h-field-message`, which is also
          the line box the message itself is set in, so reserve and content agree
          to the pixel. The message is `--text-sm`: the most urgent copy in a
          field cannot be set smaller than the label it is correcting, and a
          fourth type size in a form region is one too many. */}
      <div
        className={cn(
          reserveMessage && 'min-h-[var(--h-field-message)]',
          !reserveMessage && !invalid && !hint && 'hidden',
        )}
      >
        {invalid ? (
          <p
            id={errorId}
            role="alert"
            className="flex items-start gap-1 text-sm text-danger leading-[var(--h-field-message)]"
          >
            <AlertCircle
              aria-hidden="true"
              className="mt-[1px] size-3 shrink-0"
              strokeWidth={2.25}
            />
            <span className="min-w-0">{error}</span>
          </p>
        ) : hint ? (
          <p id={hintId} className="text-sm text-muted leading-[var(--h-field-message)]">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
